'use client'

import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { getPlaySettings } from '@/lib/phishtrainer/play-settings'
import { pfetch } from '@/lib/phishtrainer/play-api'
import { useTranslation } from '@/lib/i18n'

type Mode = 'guided' | 'expert'

interface Props {
  userId: string
}

export function ModePreferenceCard({ userId }: Props) {
  const { t } = useTranslation()
  const [allowToggle, setAllowToggle] = useState<boolean | null>(null)
  const [mode, setMode] = useState<Mode>('guided')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getPlaySettings(),
      pfetch(`/api/phishtrainer/users/${encodeURIComponent(userId)}/preferences`).then((r) => r.json()),
    ])
      .then(([settings, prefs]) => {
        if (cancelled) return
        const allow = settings?.data?.gameplay?.mode?.allowUserToggle === true
        setAllowToggle(allow)
        const adminDefault = settings?.data?.gameplay?.mode?.defaultMode ?? 'guided'
        setMode(prefs?.data?.modeDefault ?? adminDefault)
      })
      .catch(() => setAllowToggle(false))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [userId])

  if (loading || allowToggle === null) return null
  if (!allowToggle) return null

  async function save(next: Mode) {
    setSaving(true)
    setMode(next)
    try {
      await pfetch(`/api/phishtrainer/users/${encodeURIComponent(userId)}/preferences`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ modeDefault: next }),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('learner.cards.modePreference.title')}</CardTitle>
        <CardDescription>
          {t('learner.cards.modePreference.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={mode}
          onValueChange={(v) => save(v as Mode)}
          className="flex gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="guided" id="me-mode-guided" disabled={saving} />
            <Label htmlFor="me-mode-guided" className="font-normal">{t('learner.cards.modePreference.guided')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="expert" id="me-mode-expert" disabled={saving} />
            <Label htmlFor="me-mode-expert" className="font-normal">{t('learner.cards.modePreference.expert')}</Label>
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  )
}
